import {
  useEffect,
  useState,
} from 'react'

import {
  useLocation,
  useNavigate,
} from 'react-router-dom'

import {
  URLBuilder,
  reportAuthFailure,
} from '@termly-web/common'

import makeExchangeError from './makeExchangeError'
import removeFlowToken from './removeFlowToken'


/**
 * Reads the one-time flow token from the URL fragment and removes it from the
 * address.
 *
 * Removed so that a reload cannot send the spent token again and nothing that
 * later reads the address can see it. Replaced rather than pushed, keeping the
 * router's state, so that going back does not return to it either. Anything
 * else the fragment carries stays.
 *
 * The fragment can instead carry the outcome of an exchange that the flow token
 * page made and lost. That page has no way to show or report a failure itself,
 * so the outcome is reported here, and the address is replaced with the
 * session-expired page. Replaced, so that neither a reload nor going back
 * reports the same failure again.
 *
 * @returns {String|null} the token, or `null` when the page was loaded without
 *   one
 */
export default function useFlowToken() {
  const {
    hash,
    pathname,
    search,
    state,
  } = useLocation()

  const navigate = useNavigate()

  // Kept from the first render, because the address stops carrying the token
  // as soon as it is removed. A token that arrives beside a rejection is not
  // returned, because the page is leaving for the session-expired page and an
  // exchange would only put a session behind it.
  const [flowToken] = useState(() => {
    const arrival = removeFlowToken(hash)

    return ( arrival.rejection ) ? null : arrival.flowToken
  })

  useEffect(() => {
    const removal = removeFlowToken(hash)

    if ( removal.rejection ) {
      const error = makeExchangeError(removal.rejection)
      const sessionExpiredURL = URLBuilder.sessionExpiredURL()

      reportAuthFailure(error)

      navigate(sessionExpiredURL, {
        replace: true,
      })

      return
    }

    // The address changes once the token is removed, which runs this again
    // with nothing left to remove.
    if ( !removal.flowToken ) {
      return
    }

    navigate({
      hash: removal.hash,
      pathname,
      search,
    }, {
      replace: true,
      state,
    })
  }, [hash, navigate, pathname, search, state])

  return flowToken
}
